import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'

import { EmblaCarouselType } from 'embla-carousel'

import { CarouselContextState, CarouselProps } from './types'

const CarouselContext = createContext<CarouselContextState>({
  embla: undefined,
} as CarouselContextState)

export const Carousel: FC<CarouselProps> = ({
  embla,
  emblaRef,
  selectedSlideIndex: providedSelectedSlideIndex,
  children,
  transition = 'slide',
}) => {
  const viewportId = useId()
  const [dragging, setDragging] = useState(false)
  const [draggable, setDraggable] = useState(false)
  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [slides, setSlides] = useState<HTMLElement[]>([])
  const [viewedSlides, setViewedSlides] = useState<number[]>([])
  const [selectedSlide, setSelectedSlide] = useState(0)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [canScrollPrev, setCanScrollPrev] = useState(false)

  useEffect(() => {
    if (
      (selectedSlide !== 0 && !viewedSlides[selectedSlide]) ||
      (selectedSlide === 0 && viewedSlides.length === 0)
    ) {
      setViewedSlides([...viewedSlides, selectedSlide])
    }
  })

  const onInit = useCallback(() => {
    setDraggable(Boolean(embla?.canScrollNext() || embla?.canScrollPrev()))
  }, [embla])

  const onReInit = useCallback(() => {
    setDraggable(Boolean(embla?.canScrollNext() || embla?.canScrollPrev()))
    setSlidesInView(embla?.slidesInView() ?? [])
    setSelectedSlide(embla?.selectedScrollSnap() ?? 0)
    setSlides(embla?.slideNodes() ?? [])
    setCanScrollNext(embla?.canScrollNext() ?? false)
    setCanScrollPrev(embla?.canScrollPrev() ?? false)
  }, [embla])

  const onSelect = useCallback(() => {
    setDraggable(Boolean(embla?.canScrollNext() || embla?.canScrollPrev()))
    setSlidesInView(embla?.slidesInView() ?? [])
    setSelectedSlide(embla?.selectedScrollSnap() ?? 0)
    setSlides(embla?.slideNodes() ?? [])
    setCanScrollNext(embla?.canScrollNext() ?? false)
    setCanScrollPrev(embla?.canScrollPrev() ?? false)
  }, [embla])

  const onSlidesInView = useCallback(() => {
    setDraggable(Boolean(embla?.canScrollNext() || embla?.canScrollPrev()))
    setSlidesInView(embla?.slidesInView() ?? [])
    setSelectedSlide(embla?.selectedScrollSnap() ?? 0)
    setSlides(embla?.slideNodes() ?? [])
    setCanScrollNext(embla?.canScrollNext() ?? false)
    setCanScrollPrev(embla?.canScrollPrev() ?? false)
  }, [embla])

  const onPointerUp = useCallback(() => {
    setDragging(false)
  }, [])

  const onPointerDown = useCallback(() => {
    setDragging(true)
  }, [])

  useEffect(() => {
    if (embla) {
      embla.on('init', onInit)
      embla.on('reInit', onReInit)
      embla.on('pointerUp', onPointerUp)
      embla.on('pointerDown', onPointerDown)
      embla.on('select', onSelect)
      embla.on('slidesInView', onSlidesInView)
      onInit()
      onSelect()

      return () => {
        embla.off('init', onInit)
        embla.off('reInit', onReInit)
        embla.off('select', onSelect)
        embla.off('pointerUp', onPointerUp)
        embla.off('pointerDown', onPointerDown)
        embla.off('slidesInView', onSlidesInView)
      }
    }
  }, [embla, onInit, onPointerUp, onPointerDown, onSelect])

  useEffect(() => {
    if (typeof providedSelectedSlideIndex !== 'undefined') {
      embla?.scrollTo(providedSelectedSlideIndex, true)
    }
  }, [embla, providedSelectedSlideIndex])

  const context = useMemo(
    () => ({
      emblaRef,
      embla,
      viewportId,
      dragging,
      draggable,
      slides,
      slidesInView,
      selectedSlide,
      viewedSlides,
      setViewedSlides,
      canScrollNext,
      canScrollPrev,
      transition,
    }),
    [
      emblaRef,
      embla,
      viewportId,
      slides,
      viewedSlides,
      slidesInView,
      selectedSlide,
      canScrollNext,
      canScrollPrev,
      draggable,
      dragging,
      transition,
    ],
  )

  return (
    <CarouselContext.Provider value={context}>
      {children}
    </CarouselContext.Provider>
  )
}

export const useCarouselContext = () => useContext(CarouselContext)

Carousel.displayName = 'Carousel'
